import React, { useState } from "react";

const MergeCategoriesModal = ({
  isOpen,
  onClose,
  sourceCategory,
  sourceIndex,
  standardizedValues,
  valueMapping,
  onMerge,
}) => {
  const [selectedCategoriesToMerge, setSelectedCategoriesToMerge] = useState(
    [],
  );
  const [categorySearchQuery, setCategorySearchQuery] = useState("");

  const handleClose = () => {
    setSelectedCategoriesToMerge([]);
    setCategorySearchQuery("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-gray-600/10 backdrop-blur-[1px] z-50"
        onClick={handleClose}
      />
      <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-lg border border-gray-100 p-6 max-w-2xl w-full z-50">
        <div className="flex justify-between items-start mb-4">
          <div className="flex flex-col">
            <h3 className="text-left text-lg font-semibold text-gray-800 mb-1">
              Merge Categories
            </h3>
            <p className="text-md text-gray-600">
              Select categories to merge into{" "}
              <span className="font-medium text-primary px-2 py-0.5 bg-primary/10 rounded border border-primary/20">
                {sourceCategory}
              </span>
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 ml-4"
          >
            <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        <div className="mb-3">
          <input
            type="text"
            placeholder="Search categories..."
            className="w-full p-2 border rounded text-sm focus:border-primary focus:ring-1 focus:ring-primary"
            value={categorySearchQuery}
            onChange={(e) => setCategorySearchQuery(e.target.value)}
          />
        </div>

        <div className="max-h-64 overflow-y-auto mb-4 border rounded">
          {standardizedValues
            .filter(
              (cat, idx) =>
                idx !== sourceIndex &&
                cat.trim() !== "" &&
                cat.toLowerCase().includes(categorySearchQuery.toLowerCase()),
            )
            .map((category, index) => {
              const isSelected = selectedCategoriesToMerge.includes(category);
              return (
                <div
                  key={`merge-option-${index}`}
                  className={`p-3 hover:bg-gray-50 cursor-pointer ${isSelected ? "bg-primary/10 border-l-4 border-primary" : "border-b"}`}
                  onClick={() => {
                    setSelectedCategoriesToMerge((prev) =>
                      isSelected
                        ? prev.filter((cat) => cat !== category)
                        : [...prev, category],
                    );
                  }}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-4 h-4 rounded border ${isSelected ? "border-primary bg-primary" : "border-gray-300"}`}
                    >
                      {isSelected && (
                        <svg
                          className="w-4 h-4 text-white"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </div>
                    <span className="text-sm">{category}</span>
                    <span className="text-xs text-gray-500 ml-auto">
                      {(valueMapping[category] || []).length} values
                    </span>
                  </div>
                </div>
              );
            })}
        </div>

        {selectedCategoriesToMerge.length > 0 && (
          <div className="mb-4 flex flex-wrap gap-2">
            <div className="text-xs text-gray-500 mr-1">Selected:</div>
            {selectedCategoriesToMerge.map((category, index) => (
              <div
                key={`selected-${index}`}
                className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full flex items-center gap-1"
              >
                {category}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedCategoriesToMerge((prev) =>
                      prev.filter((cat) => cat !== category),
                    );
                  }}
                  className="text-primary hover:text-primary-dark"
                >
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              if (selectedCategoriesToMerge.length > 0) {
                onMerge(sourceCategory, sourceIndex, selectedCategoriesToMerge);
                setSelectedCategoriesToMerge([]);
                setCategorySearchQuery("");
              }
            }}
            disabled={selectedCategoriesToMerge.length === 0}
            className={`px-4 py-2 text-sm font-medium text-white rounded-md ${selectedCategoriesToMerge.length > 0 ? "bg-primary hover:bg-primary-dark" : "bg-gray-300 cursor-not-allowed"}`}
          >
            Confirm merge {selectedCategoriesToMerge.length} Categories
          </button>
        </div>
      </div>
    </>
  );
};

export default MergeCategoriesModal;
